import { IconButton, styled, Tooltip } from "@mui/material";
import { ReactNode, Ref } from "react";

import { PROCORE_BOTTOM_ENTRY, TOOLBAR_BUTTON, TOOLBAR_COLORS } from "../constants";

const StyledButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
    width: `${PROCORE_BOTTOM_ENTRY.ICON_SIZE}px`,
    height: `${PROCORE_BOTTOM_ENTRY.ICON_SIZE}px`,
    padding: 0,
    borderRadius: `${TOOLBAR_BUTTON.RADIUS}px`,
    backgroundColor: active ? TOOLBAR_COLORS.ACTIVE_BG : "transparent",
    color: active ? TOOLBAR_COLORS.ACTIVE : TOOLBAR_COLORS.DEFAULT,
    transition: "background-color 0.15s ease",
    "&:hover": {
        backgroundColor: active ? TOOLBAR_COLORS.ACTIVE_HOVER : TOOLBAR_COLORS.HOVER,
    },
    "&:disabled": {
        color: TOOLBAR_COLORS.DISABLED,
        backgroundColor: "transparent",
    },
    "& svg": {
        width: "24px",
        height: "24px",
    },
}));

const ButtonWrapper = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: ${PROCORE_BOTTOM_ENTRY.WIDTH}px;
`;

const ButtonLabel = styled("span", {
    shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
    width: `${PROCORE_BOTTOM_ENTRY.TEXT_WIDTH}px`,
    height: `${PROCORE_BOTTOM_ENTRY.TEXT_HEIGHT}px`,
    fontSize: "10px",
    fontWeight: 500,
    color: active ? TOOLBAR_COLORS.ACTIVE : TOOLBAR_COLORS.DEFAULT,
    textAlign: "center",
    lineHeight: `${PROCORE_BOTTOM_ENTRY.TEXT_HEIGHT}px`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}));

interface BottomToolbarButtonProps {
    /** Icon to display */
    icon: ReactNode;
    /** Label shown below the button */
    label: string;
    /** Tooltip text (should not be required for mobile, but is kept for mobile+mouse users) */
    tooltip?: string;
    /** Whether button is in active state */
    active?: boolean;
    /** Whether button is disabled */
    disabled?: boolean;
    /** Click handler */
    onClick?: () => void;
    /** Optional id to expose on DOM for anchoring menus/popovers */
    buttonId?: string;
    /** Optional ref forwarded to the underlying icon button element */
    buttonRef?: Ref<HTMLButtonElement>;
}

export function BottomToolbarButton({
    icon,
    label,
    tooltip,
    active,
    disabled,
    onClick,
    buttonId,
    buttonRef,
}: BottomToolbarButtonProps) {
    const button = (
        <ButtonWrapper>
            <StyledButton
                ref={buttonRef}
                active={active}
                disabled={disabled}
                onClick={onClick}
                data-bottom-toolbar-button-id={buttonId}
            >
                {icon}
            </StyledButton>
            <ButtonLabel active={active}>{label}</ButtonLabel>
        </ButtonWrapper>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} placement="top">
                {button}
            </Tooltip>
        );
    }

    return button;
}
